import { Button, SimpleGrid, Stack, Text, Title } from '@mantine/core';
import { IconPencil } from '@tabler/icons-react';
import { createFileRoute, Link } from '@tanstack/react-router';

import { PageLayout } from '@/components/PageLayout';
import { Tile } from '@/components/Tile';
import { useIsMobile } from '@/hooks/useIsMobile';

export const Route = createFileRoute('/')({
  component: Home,
});

const EXAMPLE_CHARTS = [
  {
    title: 'Preflop Equity',
    description: "Visualize the strength of every starting hand in Texas Hold'em",
    path: '/preflop-equity',
    preview: '/thumbs/preflop-equity.webp',
  },
  {
    title: 'Sklansky-Chubukov',
    description: 'All-in or fold ranges based on push/fold mathematics',
    path: '/sklansky-chubukov',
    preview: '/thumbs/sklansky-chubukov.webp',
  },
  {
    title: '6-Max Opening',
    description: 'Recommended opening ranges for 6-max cash games',
    path: '/sixmax-opening',
    preview: '/thumbs/sixmax-opening.webp',
  },
  {
    title: '8-Max Opening',
    description: 'Recommended opening ranges for 8-max cash games',
    path: '/eightmax-opening',
    preview: '/thumbs/eightmax-opening.webp',
  },
  {
    title: 'Mixed Strategy',
    description: 'Game theory optimal mixed strategy frequencies',
    path: '/mixed-strategy',
    preview: '/thumbs/mixed-strategy.webp',
  },
  {
    title: 'Hand Combos',
    description: 'See how dead cards affect the number of possible hand combinations',
    path: '/combos',
    preview: '/thumbs/hand-combos.webp',
  },
];

function Home() {
  const isMobile = useIsMobile();

  return (
    <PageLayout>
      <Stack align="center" gap="xl" maw={900} w="100%">
        <Stack align="center" gap="md">
          <Title order={1} c="white" ta="center" size={isMobile ? 'h2' : 'h1'}>
            Handscape
          </Title>

          <Text c="dimmed" size="lg" ta="center" maw={600}>
            Experience poker hand charts in a whole new dimension. Explore 3D visualizations that reveal patterns and
            relationships in hand ranges and strategies.
          </Text>
        </Stack>

        <Stack gap="lg" w="100%">
          <div>
            <Title order={2} size="h4" c="white" mb="md">
              Example Charts
            </Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" style={{ alignItems: 'stretch' }}>
              {EXAMPLE_CHARTS.map((chart) => (
                <Link
                  key={chart.path}
                  to={chart.path}
                  style={{
                    textDecoration: 'none',
                    display: 'flex',
                  }}
                >
                  <Tile style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <div
                      style={{
                        flex: 1,
                        minHeight: 200,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={chart.preview}
                        alt={`${chart.title} - ${chart.description}`}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                    </div>
                    <Stack gap="sm" p="lg">
                      <Title order={3} size="h5" c="white">
                        {chart.title}
                      </Title>
                      <Text c="dimmed" size="sm">
                        {chart.description}
                      </Text>
                    </Stack>
                  </Tile>
                </Link>
              ))}
            </SimpleGrid>
          </div>

          <div>
            <Title order={2} size="h4" c="white" mb="md">
              Create Your Own
            </Title>
            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              <Link
                to="/custom-chart"
                style={{
                  textDecoration: 'none',
                  display: 'block',
                }}
              >
                <Tile padding="24px">
                  <Stack gap="sm">
                    <IconPencil size={32} color="var(--mantine-color-blue-4)" />
                    <Title order={3} size="h5" c="white">
                      Custom Chart Editor
                    </Title>
                    <Text c="dimmed" size="sm">
                      Build and share your own 3D poker charts with others.
                    </Text>
                  </Stack>
                </Tile>
              </Link>
            </SimpleGrid>
          </div>
        </Stack>

        <Button component={Link} to="/about" variant="subtle" size="sm" c="dimmed">
          Learn more about this project
        </Button>
      </Stack>
    </PageLayout>
  );
}
